import React, { useEffect, useMemo, useRef, useState } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-json";
import "prismjs/themes/prism-tomorrow.css"; // looks much better than default

function pretty(obj) {
    return JSON.stringify(obj, null, 2);
}

export default function JsonAdvancedEditor({
                                               open,
                                               title = "JSON Editor",
                                               initialObject,
                                               schemaVersionExpected = 1,
                                               onApply,
                                               onClose,
                                           }) {
    const [text, setText] = useState("");
    const [error, setError] = useState("");

    const textareaRef = useRef(null);
    const preRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setText(pretty(initialObject));
        setError("");
        // reset scroll
        setTimeout(() => {
            if (textareaRef.current) textareaRef.current.scrollTop = 0;
            if (preRef.current) preRef.current.scrollTop = 0;
        }, 0);
    }, [open, initialObject]);

    const highlighted = useMemo(() => {
        try {
            // Prism expects string; escape is handled by Prism
            return Prism.highlight(text, Prism.languages.json, "json");
        } catch {
            return text;
        }
    }, [text]);

    const syncScroll = () => {
        if (!textareaRef.current || !preRef.current) return;
        preRef.current.scrollTop = textareaRef.current.scrollTop;
        preRef.current.scrollLeft = textareaRef.current.scrollLeft;
    };

    const validateAndParse = () => {
        setError("");
        try {
            const parsed = JSON.parse(text);

            if (!parsed || typeof parsed !== "object") {
                setError("JSON باید یک آبجکت باشد.");
                return null;
            }
            if (parsed.schemaVersion !== schemaVersionExpected) {
                setError(`schemaVersion باید ${schemaVersionExpected} باشد.`);
                return null;
            }
            if (!Array.isArray(parsed.fragments)) {
                setError("fragments باید آرایه باشد.");
                return null;
            }

            return parsed;
        } catch (e) {
            setError("JSON نامعتبر است: " + e.message);
            return null;
        }
    };

    const handleFormat = () => {
        const parsed = validateAndParse();
        if (!parsed) return;
        setText(pretty(parsed));
    };

    const handleApply = () => {
        const parsed = validateAndParse();
        if (!parsed) return;
        onApply(parsed);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 ltr flex items-center justify-center bg-black/50 backdrop-blur">
            <div className="w-[980px] max-w-[96vw] rounded-2xl bg-[#0b0f14] border border-white/10 shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <div className="text-white font-bold">{title}</div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleFormat}
                            className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm"
                        >
                            Format
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mx-4 mt-3 p-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
                        {error}
                    </div>
                )}

                <div className="p-4">
                    <div className="relative rounded-xl border border-white/10 overflow-hidden">
                        {/* highlighted layer */}
                        <pre
                            ref={preRef}
                            className="m-0 p-4 text-sm leading-6 overflow-auto"
                            style={{
                                maxHeight: "62vh",
                                background: "transparent",
                            }}
                            dangerouslySetInnerHTML={{ __html: highlighted }}
                        />

                        {/* textarea layer */}
                        <textarea
                            ref={textareaRef}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onScroll={syncScroll}
                            spellCheck={false}
                            className="absolute inset-0 w-full h-full m-0 p-4 text-sm leading-6 font-mono bg-transparent text-transparent caret-white outline-none resize-none overflow-auto"
                            style={{
                                maxHeight: "62vh",
                            }}
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Apply
                        </button>
                    </div>

                    <div className="text-xs text-white/50 mt-2">
                        Tip: Apply changes become undoable in the main editor.
                    </div>
                </div>
            </div>
        </div>
    );
}
